import React, {useState, useEffect} from "react";
import { Button, Form, Row, Col, Table, Card, Modal, Pagination } from "react-bootstrap";
import { Pie } from "react-chartjs-2";
import { useAccounts } from "../../contextjs/AccountContext";
import { useTransactions } from "../../contextjs/TransactionContext";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
ChartJS.register(ArcElement, Tooltip, Legend);

const currentAssetAccountNames = ["Cash", "Kenya Commercial Bank (KCB)", "Equity Bank", "George Mpesa",
    "Mpesa Till Account", "NCBA Bank"];
// defined outside CurrentAssets to avoid re-creating the array on each render

const CurrentAssets = () => {
    const { accounts } = useAccounts();
    const { transactions, createNewTransaction, loadTransactions, totalCount } = useTransactions();

    const [currentAssetsAccounts, setCurrentAssetsAccounts] = useState([]);
    const [assetData, setAssetData] = useState({});
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [transfer, setTransfer] = useState({
        from_account: '',
        to_account: '',
        amount: 0,
        transaction_date: new Date().toISOString().split('T')[0],
        particulars: ''
    });

    // const [transfers, setTransfers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const entriesPerPage = 20;

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // filter asset accounts
    useEffect(() => {
        const assets = accounts.filter(
            (account) =>
                account.account_type_details.account_type_name === "Asset" &&
                currentAssetAccountNames.includes(account.account_name)
        );
        setCurrentAssetsAccounts(assets);
    }, [accounts]);

    useEffect(() => {
        const data = {};
        currentAssetsAccounts.forEach((account) => {
            data[account.account_name] = (account.computed_balance || 0);
        });
        setAssetData(data);
    }, [currentAssetsAccounts]);

    // data for pie chart
    const pieData = {
        labels: Object.keys(assetData),
        datasets: [
            {
                data: Object.values(assetData),
                backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#9b59b6",
                    "#e74c3c", "#2ecc71"],
            },
        ],
    };

    useEffect(() => {
        const filters = { reference: 'Transfer' }; // server keeps only transfers
        if (startDate) filters.start_date = startDate;
        if (endDate) filters.end_date = endDate;

        loadTransactions(currentPage, entriesPerPage, filters);
    }, [currentPage, startDate, endDate, loadTransactions]);

    // jump back to page 1 on date filter change
    useEffect(() => setCurrentPage(1), [startDate, endDate]);

    const currentTransfers = transactions.filter(tx => tx.reference === 'Transfer');
    const totalPages = Math.ceil(totalCount / entriesPerPage);

    const handleShowTransferModal = () => setShowTransferModal(true);
    const handleCloseTransferModal = () => setShowTransferModal(false);

    const handleTransferInputChange = (e) => {
        const { name, value } = e.target;
        setTransfer({ ...transfer, [name]: value });
    };

    const handleTransferSubmit = async (e) => {
        e.preventDefault();

        const { from_account, to_account, amount, transaction_date, particulars } = transfer;

        if (from_account === to_account) {
            alert("Cannot transfer to the same account");
            return;
        }

        if (amount <= 0) {
            alert("Amount must be greater than zero");
            return;
        }

        if (from_account === "" || to_account === "" || amount === "" || transaction_date === "") {
            alert("All fields are required");
            return;
        }

        const transactionItems = [
            {
                account: from_account,
                item_description: particulars,
                amount: parseFloat(amount),
                transaction_type: "CREDIT"
            },
            {
                account: to_account,
                item_description: particulars,
                amount: parseFloat(amount),
                transaction_type: "DEBIT"
            },
        ];

        const newTransaction = {
            transaction_date,
            reference: 'Transfer',
            description: particulars,
            amount: parseFloat(amount),
            items: transactionItems,
        };
        await createNewTransaction(newTransaction);

        setTransfer({
            from_account: '',
            to_account: '',
            amount: 0,
            transaction_date: new Date().toISOString().split('T')[0],
            particulars: ''
        });
        handleCloseTransferModal();
        // loadTransactions();
        setCurrentPage(1); // reset to first page after adding transfer
    };

    const getAccountBalance = (accountId) => {
        const account = accounts.find((account) => account.id === parseInt(accountId))
        if (account) {
            const balance = parseFloat(account.computed_balance || 0);
            const balanceLabel = balance >= 0 ? 'Dr.' : 'Cr.';
            return `${balanceLabel} Ksh ${Math.abs(balance)}`;
        }
        return '';
    };

    return (
        <div className="container mt-5">
            <h1>Current Assets</h1>
            {/*balances card and pie chart*/}
            <Row className="mt-5">
                <Col md={6}>
                    <Card>
                        <Card.Body>
                            <Card.Title>Current Asset Breakdown</Card.Title>
                            <Pie data={pieData} />
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="text-white bg-info mb-4">
                        <Card.Body>
                            <Card.Title>Current Asset Balances</Card.Title>
                            {currentAssetsAccounts.map((account) => (
                                <Card.Text key={account.id}>
                                    {account.account_name}: Ksh{" "}
                                    {parseFloat(account.computed_balance).toLocaleString()}
                                </Card.Text>
                            ))}
                            <Card.Text className="text-black font-weight-bold">
                                Total current assets: Ksh{" "}
                                {currentAssetsAccounts
                                    .reduce((sum, account) => sum + parseFloat(account.computed_balance || 0), 0)
                                    .toLocaleString()}
                            </Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/*transfer button*/}
            <Button variant="primary" onClick={handleShowTransferModal} className="mb-4">
               Add Transfer
            </Button>

            {/*transfer modal*/}
            <Modal show={showTransferModal} onHide={handleCloseTransferModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Add Transfer</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleTransferSubmit}>
                        <Row>
                            <Col md={6}>
                                <Form.Group controlId="fromAccount">
                                    <label className='form-label'>Transfer Funds From</label>
                                    <Form.Control as="select" name="from_account" value={transfer.from_account}
                                                  onChange={handleTransferInputChange}>
                                        <option value="">Select account</option>
                                        {currentAssetsAccounts.map((account) => (
                                            <option key={account.id} value={account.id}>
                                                {account.account_name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                    {transfer.from_account && (
                                        <Form.Text>
                                            Current balance: {getAccountBalance(transfer.from_account)}
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="toAccount">
                                    <Form.Label>Transfer Funds To</Form.Label>
                                    <Form.Control as="select" name="to_account" value={transfer.to_account}
                                                  onChange={handleTransferInputChange}>
                                        <option value="">Select account</option>
                                        {currentAssetsAccounts.map((account) => (
                                            <option key={account.id} value={account.id}>
                                                {account.account_name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                    {transfer.to_account && (
                                        <Form.Text>
                                            Current balance: {getAccountBalance(transfer.to_account)}
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group controlId="amount">
                                    <Form.Label>Amount</Form.Label>
                                    <Form.Control type="number" name="amount" value={transfer.amount}
                                                  onChange={handleTransferInputChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="transactionDate">
                                    <Form.Label>Transfer Date</Form.Label>
                                    <Form.Control type="date" name="transaction_date" value={transfer.transaction_date}
                                                  onChange={handleTransferInputChange} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group controlId="particulars" className="mt-3">
                            <Form.Label>Particulars</Form.Label>
                            <Form.Control type="text" name="particulars" value={transfer.particulars}
                                          onChange={handleTransferInputChange} />
                        </Form.Group>
                        <Button type="submit" variant="primary" className="mt-3 float-end">
                            Save Transfer
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>

            {/*transfer table*/}
            <Row className="mb-4">
                <Col md={5}>
                    <Form.Group controlId="startDate">
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={5}>
                    <Form.Group controlId="endDate">
                        <Form.Label>End Date</Form.Label>
                        <Form.Control type="date" value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)} />
                    </Form.Group>
                </Col>
                <Col md={2} className="d-flex align-items-end">
                    <Button variant="primary" onClick={() => setCurrentPage(1)}>
                        Filter
                    </Button>
                </Col>
            </Row>
            <h2 className="mt-5">Transfers</h2>
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>From Account</th>
                        <th>To Account</th>
                        <th>Amount</th>
                        <th>Particulars</th>
                    </tr>
                </thead>
                <tbody>
                    {currentTransfers.map((transaction) => {
                        const debitItem = transaction.items.find((item) => item.transaction_type === "DEBIT");
                        const creditItem = transaction.items.find((item) => item.transaction_type === "CREDIT");
                        return (
                            <tr key={transaction.id}>
                                <td>{new Date(transaction.transaction_date).toLocaleDateString('en-GB')}</td>
                                <td>{creditItem?.account_details.account_name}</td>
                                <td>{debitItem?.account_details.account_name}</td>
                                <td>Ksh {transaction.amount}</td>
                                <td>{transaction.description}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>

            <Pagination className="justify-content-center mt-4">
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1} />
                {(() => {
                    const maxLinks = 5; // maximum number of page links to show
                    let start = Math.max(currentPage - 2, 1);
                    let end = start + maxLinks - 1;
                    if (end > totalPages) {
                        end = totalPages;
                        start = Math.max(end - maxLinks + 1, 1);
                    }
                    return Array.from({length: end - start + 1}, (_, i) => start + i)
                        .map(page => (
                            <Pagination.Item key={page} active={page === currentPage}
                                             onClick={() => setCurrentPage(page)}>
                                {page}
                            </Pagination.Item>
                        ));
                })()}
                <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages} />
            </Pagination>
        </div>
    );
};

export default CurrentAssets;